import { useCallback, useRef } from 'react';

/** Filter keys that hold string[] values and support array toggling. */
type ArrayFilterKey = 'courts' | 'domains' | 'themas' | 'wetsartikelen';

/**
 * Custom hook for handling filter toggle logic with name tracking.
 *
 * Uses a ref to read the latest selectedIds, avoiding a stale closure
 * race condition where rapid clicks could cause earlier selections to
 * be overwritten (the callback would capture an outdated array from
 * before React re-rendered).
 *
 * @param selectedIds - Currently selected IDs
 * @param updateFilter - Function to update the filter in the store
 * @param setName - Function to set/track the name for an ID
 * @param filterKey - The key of the filter in the store (must be an array filter)
 * @returns Object with toggle and bulk change handlers
 */
export function useFilterToggle(
  selectedIds: string[],
  updateFilter: (filterKey: ArrayFilterKey, value: string[]) => void,
  setName: (id: string, name: string) => void,
  filterKey: ArrayFilterKey
) {
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const handleToggle = useCallback(
    (id: string, name?: string) => {
      const currentIds = selectedIdsRef.current;
      const newSelection = currentIds.includes(id)
        ? currentIds.filter(existingId => existingId !== id)
        : [...currentIds, id];

      updateFilter(filterKey, newSelection);

      if (name && newSelection.includes(id)) {
        setName(id, name);
      }
    },
    [updateFilter, setName, filterKey]
  );

  const handleBulkChange = useCallback(
    (ids: string[], names?: Record<string, string>) => {
      updateFilter(filterKey, ids);

      if (names) {
        Object.entries(names).forEach(([id, name]) => {
          setName(id, name);
        });
      }
    },
    [updateFilter, setName, filterKey]
  );

  return { handleToggle, handleBulkChange };
}
